import { _API } from "../utils";

const toScopeValue = (value) =>
  value === undefined || value === null || value === "" ? undefined : String(value);

const unwrapApiData = (response = {}) => response.data || response;

const compactDefinedFields = (fields = {}) =>
  Object.keys(fields).reduce((result, key) => {
    if (fields[key] !== undefined) {
      result[key] = fields[key];
    }
    return result;
  }, {});

export const normalizeVodCaptionGateState = (input = {}, fallback = { state: "open" }) => {
  const source = input?.raw || input?.data || input;
  const error = `${source?.error || input?.error || ""}`;
  const failureReason = source?.failureReason || source?.message || input?.message || "";
  const blockedGate = error.match(/^(retention|legal|cost)_gate_blocked$/);

  if (source?.gateState || source?.gate) {
    const gate = source.gateState || source.gate;
    return {
      state: gate.state || (gate.allowed === false ? "blocked" : "open"),
      retentionAllowed: gate.retentionAllowed,
      legalAllowed: gate.legalAllowed,
      costAllowed: gate.costAllowed,
      failureReason: gate.failureReason || failureReason || null,
    };
  }

  if (blockedGate) {
    return {
      state: "blocked",
      retentionAllowed: blockedGate[1] === "retention" ? false : undefined,
      legalAllowed: blockedGate[1] === "legal" ? false : undefined,
      costAllowed: blockedGate[1] === "cost" ? false : undefined,
      failureReason: failureReason || error,
    };
  }

  return fallback;
};

const isPullableCaptionInputUrl = (value) => {
  const inputUrl = `${value || ""}`.trim();
  if (!inputUrl) return false;
  if (inputUrl.startsWith("blob:") || inputUrl.startsWith("data:")) return false;
  return /^(https?:|rtmps?:)/i.test(inputUrl);
};

const collectMediaElementUrls = (mediaElement) => {
  if (!mediaElement) return [];

  const sourceElements = mediaElement.querySelectorAll
    ? Array.from(mediaElement.querySelectorAll("source"))
    : [];

  return [
    mediaElement.currentSrc,
    mediaElement.src,
    mediaElement.getAttribute && mediaElement.getAttribute("src"),
    mediaElement.getAttribute && mediaElement.getAttribute("data-src"),
    mediaElement.getAttribute && mediaElement.getAttribute("data-file"),
    ...sourceElements.map((source) => source.src || source.getAttribute("src")),
  ];
};

const collectPlayerDeclaredUrls = (root = document) => {
  if (!root || !root.querySelectorAll) return [];

  return Array.from(root.querySelectorAll("[data-live-caption-input-url]"))
    .map((element) => element.dataset && element.dataset.liveCaptionInputUrl);
};

const collectJwPlayerUrls = (windowLike = window, root = document) => {
  if (!windowLike || typeof windowLike.jwplayer !== "function") return [];

  const candidates = [];
  const pushPlaylistItem = (player) => {
    if (!player || typeof player.getPlaylistItem !== "function") return;
    const item = player.getPlaylistItem() || {};
    candidates.push(item.file);
    if (Array.isArray(item.sources)) {
      item.sources.forEach((source) => candidates.push(source.file));
    }
  };

  try {
    pushPlaylistItem(windowLike.jwplayer());
  } catch (_) {}

  if (root && root.querySelectorAll) {
    Array.from(root.querySelectorAll("[id]")).forEach((element) => {
      try {
        pushPlaylistItem(windowLike.jwplayer(element.id));
      } catch (_) {}
    });
  }

  return candidates;
};

export const findActiveCaptionMediaElement = (root = document) => {
  if (!root || !root.querySelectorAll) return null;

  const mediaElements = Array.from(root.querySelectorAll("video, audio"));
  return (
    mediaElements.find((element) => !element.paused && !element.ended && element.readyState > 0) ||
    mediaElements.find((element) => element.readyState > 0) ||
    mediaElements[0] ||
    null
  );
};

export const resolveLiveCaptionInputUrl = ({
  root = document,
  windowLike = window,
} = {}) => {
  const mediaElement = findActiveCaptionMediaElement(root);
  const candidates = [
    ...collectPlayerDeclaredUrls(root),
    ...collectJwPlayerUrls(windowLike, root),
    ...collectMediaElementUrls(mediaElement),
  ]
    .map((value) => `${value || ""}`.trim())
    .filter(Boolean);
  const inputUrl = candidates.find(isPullableCaptionInputUrl);

  if (inputUrl) return inputUrl;

  throw new Error("실시간 자막 입력 URL을 찾을 수 없습니다. HLS/RTMP 원본 URL이 필요합니다.");
};

export const clearLiveCaptionOverlay = (root = document) => {
  if (!root || !root.querySelectorAll) return;
  // PoC 오버레이는 runtime 생성 DOM이라 직접 비운다
  Array.from(root.querySelectorAll("[data-internal-poc-stt-overlay]")).forEach((overlay) => {
    overlay.textContent = "";
    overlay.classList.add("hidden");
  });
  // React 오버레이([data-live-caption-overlay], Common/LiveCaptionOverlay.js)는 DOM을 직접
  // 건드리면 reconciliation이 깨지므로 이벤트로 state 정리를 요청한다
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new CustomEvent("livee:live-caption-overlay-clear"));
  }
};

export const createLiveCaptionSession = async ({
  siteIdx,
  eventIdx,
  sessionIdx,
  mode = "auto",
  inputUrl,
  provider = "openai",
  model = "gpt-4o-realtime-preview",
  language = "ko",
  api = _API,
} = {}) => {
  const response = await api.post({
    path: "/v1/captions/sessions",
    isNoErrorNotify: true,
    data: {
      siteIdx: toScopeValue(siteIdx),
      eventIdx: toScopeValue(eventIdx),
      sessionIdx: toScopeValue(sessionIdx),
      mode,
      inputUrl,
      provider,
      model,
      language,
    },
  });

  return unwrapApiData(response);
};

export const startLiveCaptionSession = async ({
  captionSessionId,
  siteIdx,
  eventIdx,
  sessionIdx,
  inputUrl,
  provider = "openai",
  model = "gpt-4o-realtime-preview",
  language = "ko",
  api = _API,
} = {}) => {
  if (!captionSessionId) {
    throw new Error("실시간 자막 세션 ID가 없습니다.");
  }
  if (!inputUrl) {
    throw new Error("실시간 자막 입력 URL이 없습니다.");
  }

  const response = await api.post({
    path: `/v1/captions/sessions/${captionSessionId}/start`,
    isNoErrorNotify: true,
    data: {
      siteIdx: toScopeValue(siteIdx),
      eventIdx: toScopeValue(eventIdx),
      sessionIdx: toScopeValue(sessionIdx),
      inputUrl,
      provider,
      model,
      language,
    },
  });

  return unwrapApiData(response);
};

export const stopLiveCaptionSession = async ({
  captionSessionId,
  siteIdx,
  eventIdx,
  sessionIdx,
  api = _API,
} = {}) => {
  if (!captionSessionId) return null;

  const response = await api.post({
    path: `/v1/captions/sessions/${captionSessionId}/stop`,
    isNoErrorNotify: true,
    data: {
      siteIdx: toScopeValue(siteIdx),
      eventIdx: toScopeValue(eventIdx),
      sessionIdx: toScopeValue(sessionIdx),
    },
  });

  return unwrapApiData(response);
};

export const updateVodCaptionSegment = async ({
  segmentId,
  text,
  speakerLabel,
  timecode,
  auditReason = "console_vod_caption_inline_edit",
  api = _API,
} = {}) => {
  if (!segmentId) {
    throw new Error("자막 Segment ID가 없습니다.");
  }

  const response = await api.patch({
    path: `/v1/captions/segments/${segmentId}`,
    isNoErrorNotify: true,
    data: compactDefinedFields({
      text,
      speakerLabel,
      timecode,
      auditReason,
    }),
  });

  return unwrapApiData(response);
};

export const createVodCaptionExport = async ({
  captionSessionId,
  format = "vtt",
  includeSpeakerLabels = true,
  api = _API,
} = {}) => {
  if (!captionSessionId) {
    throw new Error("자막 세션 ID가 없습니다.");
  }

  const response = await api.post({
    path: `/v1/captions/sessions/${captionSessionId}/exports`,
    isNoErrorNotify: true,
    data: {
      format,
      includeSpeakerLabels,
    },
  });

  return unwrapApiData(response);
};

export const downloadVodCaptionExport = async ({
  exportId,
  api = _API,
} = {}) => {
  if (!exportId) {
    throw new Error("자막 Export ID가 없습니다.");
  }

  const response = await api.get({
    path: `/v1/captions/exports/${exportId}/download`,
    isNoErrorNotify: true,
  });

  return unwrapApiData(response);
};

export const listVodCaptionExports = async ({
  captionSessionId,
  limit,
  cursor,
  api = _API,
} = {}) => {
  if (!captionSessionId) {
    throw new Error("자막 세션 ID가 없습니다.");
  }

  const response = await api.get({
    path: `/v1/captions/sessions/${captionSessionId}/exports`,
    isNoErrorNotify: true,
    data: compactDefinedFields({
      limit,
      cursor,
    }),
  });

  return unwrapApiData(response);
};

export const listVodCaptionJobs = async ({
  captionSessionId,
  limit,
  cursor,
  api = _API,
} = {}) => {
  if (!captionSessionId) {
    throw new Error("자막 세션 ID가 없습니다.");
  }

  const response = await api.get({
    path: `/v1/captions/sessions/${captionSessionId}/jobs`,
    isNoErrorNotify: true,
    data: compactDefinedFields({
      limit,
      cursor,
    }),
  });

  return unwrapApiData(response);
};

export const listVodCaptionSegments = async ({
  captionSessionId,
  limit,
  cursor,
  api = _API,
} = {}) => {
  if (!captionSessionId) {
    throw new Error("자막 세션 ID가 없습니다.");
  }

  const response = await api.get({
    path: `/v1/captions/sessions/${captionSessionId}/segments`,
    isNoErrorNotify: true,
    data: compactDefinedFields({
      limit,
      cursor,
    }),
  });

  return unwrapApiData(response);
};
